<template>
  <div class="t-range" :class="[validity]">
    <label v-if="label" class="t-range__label" :for="$attrs.id">
      {{ label }}
    </label>

    <div
      class="t-range__controls"
      :class="{
        't-range__controls--disabled': checkboxValue,
      }"
    >
      <div class="t-range__slider-area">
        <div class="t-range__tooltip-wrapper">
          <div ref="slider-tooltip" class="t-range__tooltip">
            {{ tooltipText }}
          </div>
        </div>

        <div class="t-range__slider-wrapper">
          <input
            ref="slider"
            v-model="sliderValue"
            class="t-range__slider"
            type="range"
            :min="min"
            :max="max"
            :step="step"
            @input="onSliderInput"
          />
        </div>

        <div class="t-range__limits">
          <span class="t-range__limits-label t-range__limits-min">
            &lt; {{ prefix }} {{ min }}
          </span>
          <span class="t-range__limits-label t-range__limits-max">
            &gt; {{ prefix }} {{ max }}
          </span>
        </div>
      </div>

      <div class="t-range__input-area">
        <span class="t-range__input-label">
          {{ $t('field.budget.hint') }}
        </span>

        <t-text-field
          ref="input"
          v-model="textValue"
          type="number"
          :min="min"
          :max="max"
          :step="step"
          :prefix="prefix"
          class="t-range__input"
          :placeholder="placeholder || $attrs.placeholder"
          @change="onTextChange"
        />
      </div>
    </div>

    <div class="t-range__checkbox-area">
      <TSelectable
        :id="unknownValue"
        class="t-range__checkbox"
        :label="unknownValue"
        is-checkbox
        :is-selected="checkboxValue"
        @selected="onSelect"
      />
    </div>

    <FormErrorMessages :error-messages="errorMessages" />
  </div>
</template>

<script>
import FormErrorMessages from 'chimera/all/components/form/FormErrorMessages'
import { debounce } from 'chimera/all/plugins/global.functions'

export const T_RANGE_THUMB_WIDTH_PX = 24

export default {
  name: 'TRange',

  components: {
    FormErrorMessages,
  },

  inheritAttrs: false,

  props: {
    value: {
      type: [Number, String],
      default: '0',
    },

    step: {
      type: Number,
      required: true,
    },

    min: {
      type: Number,
      required: true,
    },

    max: {
      type: Number,
      required: true,
    },

    unknownValue: {
      type: String,
      default: '',
    },

    prefix: {
      type: String,
      default: '',
    },

    success: {
      type: Boolean,
      default: false,
    },

    errorMessages: {
      type: Array,
      default: () => [],
    },

    label: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },
  },

  /**
   * @returns {object}
   */
  data() {
    return {
      innerValue: this.min,
      sliderValue: this.min,
      textValue: this.min,
      checkboxValue: false,
    }
  },

  computed: {
    /**
     * @returns {string}
     */
    tooltipText() {
      const value = this.getClampedValue(this.innerValue)
      if (!this.prefix) {
        return `${value}`
      }

      return `${this.prefix} ${value}`
    },

    /**
     * @returns {string}
     */
    validity() {
      if (this.errorMessages.length > 0) {
        return 't-range--error'
      }

      if (this.success) {
        return 't-range--success'
      }

      return ''
    },
  },

  watch: {
    /**
     * @param {string} newValue
     */
    value(newValue) {
      if (newValue === this.unknownValue) {
        this.checkboxValue = true
        return
      }

      this.innerValue = this.getClampedValue(Number(newValue) || this.min)
      this.textValue = this.innerValue
      this.sliderValue = this.innerValue
      this.updateTooltip(this.innerValue)
    },
  },

  /**
   *
   */
  created() {
    this.debouncedEmitChange = debounce((value) => {
      this.$emit('input', value)
    }, 500)
  },

  methods: {
    /**
     * @param value
     */
    onSelect(value) {
      this.checkboxValue = !this.checkboxValue
      this.emitValue(this.checkboxValue ? value.id : this.innerValue)
    },

    /**
     * @param {number} value
     */
    updateInnerValue(value) {
      this.innerValue = this.getClampedValue(value)
      this.updateTooltip(this.innerValue)
      this.emitValue(this.innerValue)
    },

    /**
     * @param {number} value
     */
    emitValue(value) {
      this.debouncedEmitChange(value)
    },

    /**
     * @param {Event} event
     */
    onSliderInput(event) {
      const value = event.target.value
      this.textValue = value

      this.updateInnerValue(value)
    },

    /**
     * @param {number} value
     */
    onTextChange(value) {
      this.sliderValue = value
      this.updateInnerValue(value)
    },

    /**
     * @param {number} value
     * @returns {number}
     */
    getClampedValue(value) {
      const min = parseFloat(this.min)
      const max = parseFloat(this.max)
      if (isNaN(value) || isNaN(min) || isNaN(max)) {
        return 0
      }

      if (max === min) {
        return min
      }

      return Math.max(min, Math.min(value, max))
    },

    /**
     * @param {number} value
     */
    /**
     * Updates the tooltip position and slider background gradient based on the current value.
     * @param {number} value - The current input value.
     */
    updateTooltip(value) {
      window.requestAnimationFrame(() => {
        const slider = this.$refs.slider
        const tooltip = this.$refs['slider-tooltip']

        if (!slider || !tooltip) {
          return
        }

        const sliderWidth = slider.clientWidth
        slider.style.setProperty(
          '--t-range-thumb-width',
          `${T_RANGE_THUMB_WIDTH_PX}px`,
        )

        const tooltipWidth = tooltip.clientWidth

        const currentValue = parseFloat(value)
        const min = parseFloat(this.min)
        const max = parseFloat(this.max)

        if (isNaN(currentValue) || isNaN(min) || isNaN(max)) {
          return
        }

        if (max === min) {
          tooltip.style.left = `calc(50% - ${tooltipWidth / 2}px)`
          slider.style.background = `linear-gradient(to right, var(--t-range-track-progress-before) 50%, var(--t-range-track-progress-after) 50%)`
          return
        }

        const valueRatio = (currentValue - min) / (max - min)
        const trackSpace = sliderWidth - T_RANGE_THUMB_WIDTH_PX
        const thumbCenterPositionPx =
          trackSpace * valueRatio + T_RANGE_THUMB_WIDTH_PX / 2
        const idealTooltipLeftPx = thumbCenterPositionPx - tooltipWidth / 2
        const minLeftPx = 0
        const maxLeftPx = sliderWidth - tooltipWidth
        const clampedTooltipLeftPx = Math.max(
          minLeftPx,
          Math.min(idealTooltipLeftPx, maxLeftPx),
        )

        const finalLeftPx = Math.round(clampedTooltipLeftPx)
        slider.style.background = `linear-gradient(to right, var(--t-range-track-progress-before) ${thumbCenterPositionPx}px, var(--t-range-track-progress-after) ${thumbCenterPositionPx}px)`
        tooltip.style.left = `${finalLeftPx}px`
      })
    },
  },
}
</script>

<style lang="scss">
:root {
  --t-range-track-progress-before: color-mix(
    in srgb,
    theme('colors.primary') 80%,
    transparent
  );
  --t-range-track-progress-after: color-mix(
    in srgb,
    theme('colors.accent') 30%,
    transparent
  );
}
</style>
